import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon, Tone, WaitingInline } from '~/components/ui';
import { useT, type Messages } from '~/i18n';
import VoiceAnswer from './components/VoiceAnswer';
import ProfilePieceCard, { PieceForm, SavingLine, period, pieceTitle } from './components/ProfilePieceCard';
import {
  PIECE_KINDS,
  addPiece,
  answerGaps,
  deleteNote,
  deletePiece,
  editPiece,
  fetchProfile,
  type PieceKind,
  type ProfileGap,
  type ProfilePiece,
  type ProfileView,
  type ProfileWriteState,
} from './api/profileClient';
import { fetchCVScore } from './api/cvScoreClient';
import { scoreColorVar, scoreLabel, scoreTone } from './lib/cvScore';
import type { InputMode } from './lib/voice';
import { SCORE_GUIDE_PATH } from './lib/publicLinks';

/**
 * «Mejorar mi hoja de vida» (D10 del spec 2026-10-21-tbit-jobs-hoja-de-vida-por-piezas).
 *
 * Antes, «Agregar estos datos» mandaba al cuestionario entero y la persona tenía
 * que volver a contar todo para sumar un dato. Acá cada carencia es una tarjeta
 * con su pregunta, por qué importa y una casilla VACÍA: la respuesta nueva se
 * guarda entera en su lugar y se SUMA a lo que ya estaba, nunca lo reescribe.
 * Por eso la casilla no viene precargada: lo ya dicho vive abajo, en Mis datos.
 *
 * Las tarjetas se responden todas y se guardan JUNTAS con un solo botón: el
 * servidor procesa y recalcula una vez. Antes cada «Guardar» bloqueaba la
 * página y, al volver, cambiaba las preguntas de abajo mientras la persona
 * seguía respondiendo (video de Visa Experts Legal, 2026-10-31). «No tengo
 * esto» también se marca y viaja en el mismo lote.
 *
 * Al guardar, el puntaje se mueve con su explicación. El servidor no lo deja
 * bajar cuando se agrega (solo al editar o borrar, y entonces dice por qué).
 */

type Copy = Messages['onboarding']['improveCV'];

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; view: ProfileView }
  /**
   * Sin perfil: se ofrece el cuestionario, no un error. `replaces`: la hoja de
   * vida con que postula hoy (si la hay), para que el cuestionario abra con sus
   * respuestas precargadas como siempre (la migración todavía no la pasó a piezas).
   */
  | { kind: 'none'; replaces?: string }
  | { kind: 'error'; message: string };

type WriteOk = Extract<ProfileWriteState, { status: 'ok' }>;

/** Lo escrito después de lo que se mandó: si la casilla sigue empezando igual, solo lo nuevo. */
function afterSent(current: string, sent: string): string {
  const now = current.trim();
  if (now === sent) return '';
  return now.startsWith(sent) ? now.slice(sent.length).trim() : now;
}

/** Cada cuánto se pregunta mientras hay respuestas por repartir, y hasta cuándo (las esperas del servidor suman 10 min). */
const PENDING_POLL_MS = 5000;
const PENDING_POLL_MAX_MS = 12 * 60 * 1000;

/** Lo que se dice al guardar: «Subió 6 puntos», «Se mantuvo...», «Bajó 5 puntos: ...». */
function deltaCopy(r: WriteOk, t: Copy): string | null {
  // Todavía no se repartió: el puntaje de antes no dice nada (M2).
  if (!r.processed) return null;
  if (r.previous_score === null || r.score === null) return null;
  const diff = r.score - r.previous_score;
  if (diff > 0) return t.up(diff);
  if (diff === 0) return t.same;
  // Sin razón no se inventa una: queda el puntaje nuevo a la vista.
  return r.reason ? t.down(-diff, r.reason) : null;
}

/** Trabajo → estudio → licencia → ...: el orden de la hoja de vida. */
function byKind<T extends { kind: PieceKind }>(pieces: T[]): T[] {
  const rank = (k: PieceKind) => PIECE_KINDS.indexOf(k);
  return pieces
    .map((p, i) => ({ p, i }))
    .sort((a, b) => rank(a.p.kind) - rank(b.p.kind) || a.i - b.i)
    .map(({ p }) => p);
}

/** Las tres cosas que se agregan a mano; el resto sale de las respuestas. */
const ADDABLE: Array<{ kind: PieceKind; label: 'addJob' | 'addEducation' | 'addLicense' }> = [
  { kind: 'job', label: 'addJob' },
  { kind: 'education', label: 'addEducation' },
  { kind: 'license', label: 'addLicense' },
];

/**
 * De qué pieza es la pregunta: «Trabajo · Soldador · Rival Word · 2025-2026». El
 * modelo califica cada pieza sola y pregunta por «este trabajo» o «el
 * certificado»; con tres trabajos la persona no sabía cuál (Pedro, 2026-10-30).
 */
function gapContext(piece: ProfilePiece, t: Copy): string {
  const kind = t.kinds[piece.kind];
  const title = pieceTitle(piece, t);
  const when = period(piece, t);
  return [kind, title === kind ? '' : title, when ?? ''].filter(Boolean).join(' · ');
}

/** Lo último que respondió a esta misma carencia, si la pregunta volvió (respondió la mitad). */
function lastAnswer(gap: ProfileGap, pieces: ProfilePiece[]): string | null {
  const notes = pieces.flatMap((p) => p.notes ?? []).filter((n) => n.gap_id === gap.gap_id);
  if (!notes.length) return null;
  const text = notes[notes.length - 1].text.trim().replace(/\s+/g, ' ');
  return text.length > 140 ? `${text.slice(0, 140).trim()}…` : text;
}

interface GapCardProps {
  gap: ProfileGap;
  /** La pieza dueña de la carencia; null para las del perfil entero. */
  piece: ProfilePiece | null;
  previous: string | null;
  value: string;
  onChange: (value: string, mode: InputMode) => void;
  /** Marcada como «No tengo esto»: se manda con el lote, no al tocar. */
  dismissed: boolean;
  onToggleDismiss: () => void;
  disabled: boolean;
  error?: string | null;
}

function GapCard({ gap, piece, previous, value, onChange, dismissed, onToggleDismiss, disabled, error }: GapCardProps) {
  const t = useT('onboarding').improveCV;
  return (
    <section aria-label={gap.question} className={`card rise${dismissed ? ' opacity-70' : ''}`}>
      <div className="card-body space-y-3">
        {piece && <span className="brand-label">{gapContext(piece, t)}</span>}
        <h3 className="font-semibold">{gap.question}</h3>
        {dismissed ? (
          <p className="text-sm">{t.dismissed}</p>
        ) : (
          <>
            {previous && <p className="text-sm hush">{t.alreadySaid(previous)}</p>}
            <p className="text-sm hush">{gap.why_it_matters}</p>
            <VoiceAnswer
              id={`gap-${gap.gap_id}`}
              field={`gap.${gap.gap_id}`}
              question={gap.question}
              invitation={gap.how_to_fix || t.invitation}
              value={value}
              maxLength={4000}
              coach={false}
              onChange={onChange}
            />
          </>
        )}
        {error && <div className="alert alert-error text-sm">{error}</div>}
        <div className="flex flex-wrap gap-3">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onToggleDismiss} disabled={disabled}>
            {dismissed ? t.undoDismiss : t.dismiss}
          </button>
        </div>
      </div>
    </section>
  );
}

export default function JobApplyImproveCVPage() {
  const t = useT('onboarding').improveCV;
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  /** Lo que se está escribiendo en cada carencia, con cómo se escribió. */
  const [answers, setAnswers] = useState<Record<string, { text: string; mode: InputMode }>>({});
  /** Las carencias marcadas «No tengo esto», que viajan con el lote. */
  const [dismissed, setDismissed] = useState<Record<string, true>>({});
  /** La escritura en curso ('gap:<id>', 'piece:<id>', 'add'): una a la vez. */
  const [inFlight, setInFlight] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** El resultado del último guardado, en palabras. Se guarda la respuesta y no la frase: cambia de idioma. */
  const [lastWrite, setLastWrite] = useState<WriteOk | null>(null);
  const [adding, setAdding] = useState<PieceKind | null>(null);
  /**
   * Lo que se escribió en una casilla MIENTRAS guardaba y cuya pregunta ya no
   * está (se respondió con lo primero): no se tira, se ofrece guardarlo aparte.
   */
  const [leftovers, setLeftovers] = useState<{ id: string; text: string }[]>([]);
  /** La casilla al día, para leerla cuando vuelve una escritura (sin efectos dentro de un setState). */
  const answersRef = useRef(answers);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);
  /** Sube con cada escritura: una pregunta de fondo que vuelve después se descarta. */
  const writeGen = useRef(0);
  /** Desde cuándo se pregunta por respuestas pendientes. */
  const pollSince = useRef<number | null>(null);

  const reload = useCallback(async () => {
    setLoad({ kind: 'loading' });
    const state = await fetchProfile();
    if (state.status === 'ok') setLoad({ kind: 'ready', view: state.view });
    else if (state.status === 'no_profile') {
      setLoad({ kind: 'none' });
      // Con la hoja de vida de antes (sin piezas todavía), el cuestionario abre con sus respuestas.
      const score = await fetchCVScore();
      if (score.status === 'ok' && score.cvId) {
        setLoad((l) => (l.kind === 'none' ? { kind: 'none', replaces: score.cvId } : l));
      }
    } else setLoad({ kind: 'error', message: state.message });
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** Toda escritura pasa por acá: una a la vez, con su error en su tarjeta. */
  const write = useCallback(async (key: string, fn: () => Promise<ProfileWriteState>): Promise<WriteOk | null> => {
    writeGen.current += 1;
    setInFlight(key);
    setErrors((e) => {
      const { [key]: _gone, ...rest } = e;
      return rest;
    });
    const r = await fn();
    writeGen.current += 1;
    setInFlight(null);
    if (r.status === 'ok') {
      setLoad({ kind: 'ready', view: r.view });
      setLastWrite(r);
      return r;
    }
    // Sin perfil a mitad de camino (otra pestaña lo borró, o un 404 raro): se
    // dice en la tarjeta y NO se desmonta la pantalla, que se llevaría lo que
    // está escrito en las demás casillas. Un error del lote que nombra una
    // tarjeta (respondió con una pregunta) va a esa tarjeta.
    const errKey = r.status === 'error' && r.gap_id ? `gap:${r.gap_id}` : key;
    setErrors((e) => ({ ...e, [errKey]: r.status === 'no_profile' ? t.noProfile : r.message }));
    return null;
  }, [t]);

  const run = useCallback(
    async (key: string, fn: () => Promise<ProfileWriteState>): Promise<boolean> => (await write(key, fn)) !== null,
    [write],
  );

  /** Lo que sale en el próximo «Guardar»: respuestas escritas y «No tengo esto». */
  const batchOf = (gaps: ProfileGap[]) => {
    const sent = gaps
      .filter((g) => !dismissed[g.gap_id] && answers[g.gap_id]?.text.trim())
      .map((g) => ({ gap_id: g.gap_id, text: answers[g.gap_id].text.trim(), input_mode: answers[g.gap_id].mode }));
    const dismiss = gaps.filter((g) => dismissed[g.gap_id]).map((g) => g.gap_id);
    return { sent, dismiss };
  };

  const saveAll = async (gaps: ProfileGap[]) => {
    const { sent, dismiss } = batchOf(gaps);
    if (!sent.length && !dismiss.length) return;
    const r = await write('gaps', () => answerGaps(sent, dismiss));
    if (!r) return;
    // Lo que se mandó sale de su casilla: si la carencia sigue, la próxima
    // respuesta también se suma, y lo mandado no se manda dos veces. Lo que se
    // escribió o dictó MIENTRAS guardaba es nuevo y se queda; si su pregunta ya
    // no está, se ofrece guardarlo aparte.
    const open = new Set(r.view.gaps.map((g) => g.gap_id));
    const kept: Record<string, { text: string; mode: InputMode }> = {};
    const extra: { id: string; text: string }[] = [];
    for (const a of sent) {
      const current = answersRef.current[a.gap_id];
      const rest = current ? afterSent(current.text, a.text) : '';
      if (!rest) continue;
      if (open.has(a.gap_id)) kept[a.gap_id] = { text: rest, mode: current!.mode };
      else extra.push({ id: `${a.gap_id}:${Date.now()}`, text: rest });
    }
    const sentIds = new Set([...sent.map((a) => a.gap_id), ...dismiss]);
    setAnswers((a) => {
      const others = Object.fromEntries(Object.entries(a).filter(([id]) => !sentIds.has(id)));
      return { ...others, ...kept };
    });
    setDismissed({});
    if (extra.length) setLeftovers((l) => [...l, ...extra]);
  };

  const saveLeftover = async (item: { id: string; text: string }) => {
    const label = item.text.slice(0, 80).trim();
    const ok = await run(`leftover:${item.id}`, () => addPiece('other', { label }, item.text));
    if (ok) setLeftovers((l) => l.filter((x) => x.id !== item.id));
  };

  const view = load.kind === 'ready' ? load.view : null;
  const pieces = useMemo(() => (view ? byKind(view.pieces) : []), [view]);

  /**
   * Hay respuestas guardadas que todavía no entraron a las piezas (el modelo
   * estaba caído; el servidor reintenta con espera y al final las mete sin él).
   * Se vuelve a preguntar en silencio hasta que entren: entonces se ve el
   * puntaje nuevo. Una escritura en el medio gana: su vista es la más nueva.
   */
  useEffect(() => {
    if (!view || view.pending_inputs === 0) {
      pollSince.current = null;
      return;
    }
    if (pollSince.current === null) pollSince.current = Date.now();
    if (Date.now() - pollSince.current > PENDING_POLL_MAX_MS) return;
    const gen = writeGen.current;
    const id = setTimeout(async () => {
      const state = await fetchProfile();
      if (gen !== writeGen.current || state.status !== 'ok') return;
      setLoad({ kind: 'ready', view: state.view });
      if (state.view.pending_inputs === 0) {
        const score = typeof state.view.score?.score === 'number' ? (state.view.score.score as number) : null;
        setLastWrite((w) => (w && !w.processed ? { ...w, score, processed: true } : w));
      }
    }, PENDING_POLL_MS);
    return () => clearTimeout(id);
  }, [view]);

  if (load.kind === 'loading') return <WaitingInline label={t.loading} />;

  if (load.kind === 'error') {
    return (
      <div className="card">
        <div className="card-body py-4 flex flex-row items-center gap-3 flex-wrap">
          <p className="text-sm hush flex-1 min-w-[12rem]">{load.message}</p>
          <button type="button" className="btn btn-sm btn-outline gap-2" onClick={() => void reload()}>
            <Icon name="refresh" size={14} />
            {t.retry}
          </button>
        </div>
      </div>
    );
  }

  if (load.kind === 'none' || !view) {
    return (
      <div className="card">
        <div className="card-body space-y-3">
          <p className="text-sm">{t.noProfile}</p>
          <div className="flex flex-wrap gap-3">
            <Link
              to={
                load.kind === 'none' && load.replaces
                  ? `/onboarding/screening?replaces=${encodeURIComponent(load.replaces)}`
                  : '/onboarding/screening'
              }
              className="btn btn-primary btn-sm"
            >
              {t.toQuestionnaire}
            </Link>
            <Link to="/perfil/hoja-de-vida" className="btn btn-ghost btn-sm">
              {t.back}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const busy = inFlight !== null;
  const batch = batchOf(view.gaps);
  const batchCount = batch.sent.length + batch.dismiss.length;
  const score = typeof view.score?.score === 'number' ? (view.score.score as number) : null;
  const delta = lastWrite ? deltaCopy(lastWrite, t) : null;

  return (
    <div className="space-y-5">
      <header className="space-y-2">
        <Link to="/perfil/hoja-de-vida" className="link text-sm hush">
          {t.back}
        </Link>
        <h2 className="text-xl font-semibold">{t.title}</h2>
        <p className="hush text-sm">
          {t.intro}{' '}
          <a className="link" href={SCORE_GUIDE_PATH}>
            {t.howItWorks}
          </a>
        </p>
        {/* Con color y etiqueta del semáforo: en verde (70 o más) se entiende que ya está lista para enviar. */}
        {score !== null && (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium" style={{ color: scoreColorVar(score) }}>
              {t.score(score)}
            </p>
            <Tone tone={scoreTone(score)}>{scoreLabel(score)}</Tone>
          </div>
        )}
        {delta && (
          <p className="text-sm text-brand" role="status">
            {delta}
          </p>
        )}
        {(view.pending_inputs > 0 || lastWrite?.processed === false) && <SavingLine label={t.saving} />}
      </header>

      {leftovers.map((item) => {
        const key = `leftover:${item.id}`;
        return (
          <section key={item.id} aria-label={t.leftoverTitle} className="card rise">
            <div className="card-body space-y-3">
              <p className="text-sm font-medium">{t.leftoverTitle}</p>
              <p className="text-sm">{item.text}</p>
              {errors[key] && <div className="alert alert-error text-sm">{errors[key]}</div>}
              {inFlight === key && <SavingLine label={t.saving} />}
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={busy}
                  onClick={() => void saveLeftover(item)}
                >
                  {t.leftoverSave}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={busy}
                  onClick={() => setLeftovers((l) => l.filter((x) => x.id !== item.id))}
                >
                  {t.leftoverDiscard}
                </button>
              </div>
            </div>
          </section>
        );
      })}

      {view.gaps.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <p className="text-sm">{t.noGaps}</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {view.gaps.map((gap) => (
            <GapCard
              key={gap.gap_id}
              gap={gap}
              piece={view.pieces.find((p) => p.id === gap.piece_id) ?? null}
              previous={lastAnswer(gap, view.pieces)}
              value={answers[gap.gap_id]?.text ?? ''}
              onChange={(text, mode) => setAnswers((a) => ({ ...a, [gap.gap_id]: { text, mode } }))}
              dismissed={!!dismissed[gap.gap_id]}
              onToggleDismiss={() =>
                setDismissed((d) => {
                  const { [gap.gap_id]: was, ...rest } = d;
                  return was ? rest : { ...rest, [gap.gap_id]: true };
                })
              }
              disabled={busy}
              error={errors[`gap:${gap.gap_id}`]}
            />
          ))}
          {/* Un solo botón para todas: se queda a la vista mientras baja por las tarjetas. */}
          <div className="sticky bottom-3 z-10">
            <div className="card shadow-lg">
              <div className="card-body py-3 space-y-2">
                {errors.gaps && <div className="alert alert-error text-sm">{errors.gaps}</div>}
                {inFlight === 'gaps' && <SavingLine label={t.saving} />}
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy || batchCount === 0}
                    onClick={() => void saveAll(view.gaps)}
                  >
                    {t.saveAll(batchCount)}
                  </button>
                  <p className="text-sm hush flex-1 min-w-[12rem]">{t.saveAllHint}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Mis datos ─────────────────────────────────────────────────────────
          Lo que ya está, pieza por pieza y en el orden de la hoja de vida. Es la
          única puerta que quita palabras de la persona, y solo si ella lo pide. */}
      <section className="space-y-4" aria-labelledby="my-data-title">
        <h2 id="my-data-title" className="text-lg font-semibold">
          {t.myData}
        </h2>

        {pieces.map((piece) => {
          const key = `piece:${piece.id}`;
          return (
            <ProfilePieceCard
              key={piece.id}
              piece={piece}
              saving={inFlight === key}
              disabled={busy}
              error={errors[key]}
              onEdit={(patch) => run(key, () => editPiece(piece.id, patch))}
              onDelete={() => run(key, () => deletePiece(piece.id))}
              onDeleteNote={(noteId) => run(key, () => deleteNote(piece.id, noteId))}
            />
          );
        })}

        {adding ? (
          <div className="card rise">
            <div className="card-body">
              <PieceForm
                key={adding}
                kind={adding}
                label={t[ADDABLE.find((a) => a.kind === adding)?.label ?? 'addJob']}
                saving={inFlight === 'add'}
                disabled={busy}
                error={errors.add}
                onCancel={() => setAdding(null)}
                onSubmit={async (patch) => {
                  const ok = await run('add', () => addPiece(adding, patch.fields ?? {}, patch.text ?? ''));
                  if (ok) setAdding(null);
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {ADDABLE.map((a) => (
              <button
                key={a.kind}
                type="button"
                className="btn btn-sm btn-outline"
                disabled={busy}
                onClick={() => setAdding(a.kind)}
              >
                {t[a.label]}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
